import classNames from 'classnames';
import { useState } from 'react';

import { btn } from '@/components/ui/styles';
import type { LeadDetail } from '@/types/api';
import { formatDate } from '@/utils/format';
import http from '@/utils/http';
import { BLOCKABLE, stageLabel } from '@/utils/labels';

/**
 * What a person sets on a lead (Q4): Clarifications, a stage over the platform's
 * (PUT /leads/{id}/stage), and the blocked tag on a lead in Configuring or Clarifications
 * (PUT /leads/{id}/blocked). Clarifications lasts until the platform moves the lead on,
 * or "Back to <platform stage>"; the tag goes when the lead leaves those stages. New,
 * Configuring, Paid and Live are never set by hand (CAS-4). On a phone the buttons share
 * the width, 44px tall (UI-1).
 */
export const StageControl = ({ lead, onChange }: { lead: LeadDetail; onChange: () => void }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const call = async (path: string, body: object) => {
    setBusy(true);
    setError(null);
    try {
      await http.put(`/leads/${encodeURIComponent(lead.id)}/${path}`, body);
      onChange();
    } catch {
      setError('That did not work. Try again.');
    } finally {
      setBusy(false);
    }
  };
  const canBlock = BLOCKABLE.includes(lead.stage);
  const button = classNames(btn.secondary, 'flex-1 md:flex-none');

  return (
    <div>
      {lead.stage_override ? (
        <p key={lead.stage_override} className="animate-fade-in text-sm text-fg">
          {stageLabel(lead.stage_override)}, set by {lead.stage_override_by || 'the team'}
          {lead.stage_override_at && `, ${formatDate(lead.stage_override_at)}`}. It ends when the platform moves the lead on from{' '}
          {stageLabel(lead.platform_stage)}.
        </p>
      ) : (
        <p className="text-sm text-fg-muted">The platform has this lead at {stageLabel(lead.platform_stage)}. Mark it when they ask questions before they start.</p>
      )}
      {lead.blocked_at && (
        <p className="mt-1.5 animate-fade-in text-sm text-fg">
          <span className="text-danger">Blocked</span>, set by {lead.blocked_by || 'the team'}, {formatDate(lead.blocked_at)}. It goes when the lead leaves{' '}
          Configuring or Clarifications.
        </p>
      )}
      <div className="mt-3 flex flex-wrap gap-2">
        {lead.stage_override ? (
          <button type="button" className={button} disabled={busy} onClick={() => void call('stage', { stage: '' })}>
            Back to {stageLabel(lead.platform_stage)}
          </button>
        ) : (
          <button type="button" className={button} disabled={busy} onClick={() => void call('stage', { stage: 'clarifications' })}>
            Mark as Clarifications
          </button>
        )}
        {(canBlock || lead.blocked_at) && (
          <button type="button" className={button} disabled={busy} onClick={() => void call('blocked', { blocked: !lead.blocked_at })}>
            {lead.blocked_at ? 'Not blocked any more' : 'Mark as blocked'}
          </button>
        )}
      </div>
      {!canBlock && !lead.blocked_at && <p className="mt-2 text-xs text-fg-faint">A lead can be marked blocked in Configuring or Clarifications.</p>}
      {error && <p className="mt-2 text-sm text-danger">{error}</p>}
    </div>
  );
};
